/**
 * Admin API Client
 * Uses Firebase ID token for authentication
 * Server validates admin status via /api/auth/check-admin
 */

import { getIdToken } from "@/lib/firebase";

const API_BASE = import.meta.env.VITE_API_BASE || "";

async function getAuthHeaders(): Promise<HeadersInit> {
  console.log("%c[Admin API] 🔐 Getting auth headers...", "color: blue; font-weight: bold");

  try {
    console.log("%c[Admin API] 📞 Calling getIdToken(true)...", "color: blue");
    const token = await getIdToken(true);
    console.log("%c[Admin API] 📥 getIdToken result:", "color: blue", token ? `token (length: ${token.length})` : "null");

    if (token) {
      console.log("%c[Admin API] ✅ Using Firebase ID token for authentication", "color: green; font-weight: bold");
      const headers: HeadersInit = {
        "Authorization": `Bearer ${token}`,
      };
      console.log("%c[Admin API] 📤 Returning headers with Authorization", "color: green");
      console.log("%c[Admin API] Header preview:", "color: blue", {
        hasAuth: "Authorization" in headers,
        authLength: token.length
      });
      return headers;
    } else {
      console.warn("%c[Admin API] ⚠️ Firebase ID token is null - user may not be logged in", "color: orange; font-weight: bold");
    }
  } catch (error) {
    console.error("%c[Admin API] ❌ Failed to get Firebase token:", "color: red; font-weight: bold", error);
    console.error("%c[Admin API] Error details:", "color: red", error instanceof Error ? error.message : String(error));
    console.error("%c[Admin API] Error stack:", "color: red", error instanceof Error ? error.stack : "N/A");
  }

  // Fallback to API key if token is not available
  const adminKey = import.meta.env.VITE_ADMIN_API_KEY;
  console.log("%c[Admin API] 🔑 Checking VITE_ADMIN_API_KEY:", "color: blue", adminKey ? `set (length: ${adminKey.length})` : "not set");

  if (adminKey) {
    console.log("%c[Admin API] ⚠️ Using X-Admin-Api-Key (Firebase token not available)", "color: orange; font-weight: bold");
    return {
      "X-Admin-Api-Key": adminKey,
    };
  }

  console.error("%c[Admin API] ❌ No authentication method available", "color: red; font-weight: bold");
  console.error("%c[Admin API] - Firebase token: null", "color: red");
  console.error("%c[Admin API] - VITE_ADMIN_API_KEY:", "color: red", adminKey ? "set" : "not set");
  const error = new Error("Not authenticated. Please log in or configure VITE_ADMIN_API_KEY.");
  console.error("%c[Admin API] Throwing error:", "color: red; font-weight: bold", error.message);
  throw error;
}

export async function apiGet<T = any>(path: string): Promise<T> {
  // Force console log even if filtered
  console.log("%c[Admin API] 🚀 apiGet called with path:", "color: blue; font-weight: bold", path);

  let headers: HeadersInit;
  try {
    console.log("%c[Admin API] 🔐 Calling getAuthHeaders()...", "color: blue; font-weight: bold");
    headers = await getAuthHeaders();

    // Check headers more carefully
    const headerKeys: string[] = [];
    if (headers instanceof Headers) {
      headers.forEach((_, key) => headerKeys.push(key));
    } else if (Array.isArray(headers)) {
      headers.forEach(([key]) => headerKeys.push(key));
    } else {
      headerKeys.push(...Object.keys(headers));
    }

    console.log("%c[Admin API] ✅ Headers obtained:", "color: green; font-weight: bold", headerKeys);
    console.log("%c[Admin API] 📋 Header check:", "color: blue", {
      hasAuthorization: headerKeys.includes('Authorization'),
      hasXAdminApiKey: headerKeys.includes('X-Admin-Api-Key'),
      allKeys: headerKeys,
    });
  } catch (error) {
    console.error("%c[Admin API] ❌ Failed to get auth headers:", "color: red; font-weight: bold", error);
    throw error;
  }

  // Ensure path starts with / if API_BASE is empty (relative path)
  const url = API_BASE ? `${API_BASE}${path}` : path.startsWith('/') ? path : `/${path}`;

  console.log("[Admin API] 🌐 Making GET request to:", url);

  // Convert HeadersInit to plain object for logging
  const headersObj: Record<string, string> = {};
  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      headersObj[key] = key === 'Authorization' ? value.substring(0, 20) + '...' : value;
    });
  } else if (Array.isArray(headers)) {
    headers.forEach(([key, value]) => {
      headersObj[key] = key === 'Authorization' ? value.substring(0, 20) + '...' : value;
    });
  } else {
    Object.entries(headers).forEach(([key, value]) => {
      headersObj[key] = key === 'Authorization' ? (value as string).substring(0, 20) + '...' : value as string;
    });
  }
  console.log("[Admin API] 📤 Request headers (sanitized):", headersObj);
  console.log("[Admin API] 📤 Header keys:", Object.keys(headersObj));

  const res = await fetch(url, {
    headers: {
      ...headers,
    },
  });

  console.log("[Admin API] 📥 Response status:", res.status, res.statusText);

  if (!res.ok) {
    const errorText = await res.text();
    console.error("[Admin API] ❌ Request failed:", {
      status: res.status,
      statusText: res.statusText,
      errorText: errorText.substring(0, 200),
    });

    // Check if response is HTML (error page)
    if (errorText.trim().startsWith('<!DOCTYPE') || errorText.trim().startsWith('<html')) {
      throw new Error(`GET ${path} failed: ${res.status} - Server returned HTML instead of JSON. Check if API endpoint exists and server is running.`);
    }
    throw new Error(`GET ${path} failed: ${res.status} - ${errorText}`);
  }

  const data = await res.json();
  console.log("[Admin API] ✅ Response received:", Object.keys(data));
  return data;
}

export async function apiPost<T = any>(path: string, body: any): Promise<T> {
  const headers = await getAuthHeaders();
  const url = API_BASE ? `${API_BASE}${path}` : path.startsWith('/') ? path : `/${path}`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const errorText = await res.text();
    if (errorText.trim().startsWith('<!DOCTYPE') || errorText.trim().startsWith('<html')) {
      throw new Error(`POST ${path} failed: ${res.status} - Server returned HTML instead of JSON. Check if API endpoint exists and server is running.`);
    }
    throw new Error(`POST ${path} failed: ${res.status} - ${errorText}`);
  }

  return res.json();
}

export async function apiPatch<T = any>(path: string, body: any): Promise<T> {
  const headers = await getAuthHeaders();
  const url = API_BASE ? `${API_BASE}${path}` : path.startsWith('/') ? path : `/${path}`;

  const res = await fetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const errorText = await res.text();
    if (errorText.trim().startsWith('<!DOCTYPE') || errorText.trim().startsWith('<html')) {
      throw new Error(`PATCH ${path} failed: ${res.status} - Server returned HTML instead of JSON. Check if API endpoint exists and server is running.`);
    }
    throw new Error(`PATCH ${path} failed: ${res.status} - ${errorText}`);
  }

  return res.json();
}

export async function apiDelete<T = any>(path: string): Promise<T> {
  const headers = await getAuthHeaders();
  const url = API_BASE ? `${API_BASE}${path}` : path.startsWith('/') ? path : `/${path}`;

  const res = await fetch(url, {
    method: "DELETE",
    headers: {
      ...headers,
    },
  });

  if (!res.ok) {
    const errorText = await res.text();
    if (errorText.trim().startsWith('<!DOCTYPE') || errorText.trim().startsWith('<html')) {
      throw new Error(`DELETE ${path} failed: ${res.status} - Server returned HTML instead of JSON. Check if API endpoint exists and server is running.`);
    }
    throw new Error(`DELETE ${path} failed: ${res.status} - ${errorText}`);
  }

  return res.json();
}

export function hasAdminKey(): boolean {
  // Always return true - admin check is done server-side via Firebase token
  return true;
}
